import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, IconButton, Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { hasUiFlag, setUiFlag } from '../../utils/uiFlags';
import { setTour, PLATFORM_STEPS } from '../tour/tourState';

/*
 * First-goal guided tour. A small coach card pinned to the bottom-left of
 * the goal page that follows the goal's live lifecycle (via the polled
 * activity phases) and walks the user through the page's sections in
 * order: Driver Discovery -> Models (the tournament) -> Forecast ->
 * Scenarios -> Alerts. Each step first points at the section's nav item
 * (which pulses), then - once the user is on the section - explains what
 * they're looking at. The last step hands off to the platform tour
 * (PlatformTourGuide), which walks the workspace pages.
 *
 * Runs once per ACCOUNT (ui_flags.tour_done on the user doc - new
 * workspaces, browsers and devices never see it again); every card
 * carries an X and a "Skip tour" escape hatch. No LLM involved - it's
 * driven purely by the goal's status, so it costs nothing and reacts
 * instantly.
 */

// Legacy browser-local flag from the first release of the tour - still
// honoured so nobody who finished or skipped it sees it again.
const DONE_KEY = 'predictai.tour.goal.done';
const ackKey = (goalId) => `predictai.tour.goal.acks.${goalId}`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// Pulsing ring for the targeted nav item. Painted on a pseudo-element
// whose `color` is the theme primary, so currentColor keeps it on-brand.
export const navPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 currentColor; opacity: 0.55; }
  75%  { box-shadow: 0 0 0 8px currentColor; opacity: 0; }
  100% { box-shadow: 0 0 0 0 currentColor; opacity: 0; }
`;

const STEPS = [
  {
    id: 'drivers',
    section: 'drivers',
    title: 'Drivers found',
    prompt: (ctx) => `Discovery found ${ctx.driversLabel} that move your target. Click Driver Discovery to see them.`,
    explain: 'Solid rows are validated drivers — confirmed against your data. Dimmed ones are suggestive. These feed the model as inputs.',
  },
  {
    id: 'models',
    section: 'models',
    title: 'The model tournament',
    prompt: () => 'Now several models race on your data, each scored on the same walk-forward folds. Click Models to watch.',
    explain: 'The best score wins and is deployed automatically. You can re-run the tournament or deploy a different model here anytime.',
  },
  {
    id: 'forecast',
    section: 'forecast',
    title: 'Your forecast is live',
    prompt: () => 'The champion model is serving. Click Forecast to see what it predicts.',
    explain: 'The line is the median prediction; the shaded bands are the uncertainty range. It refreshes automatically as new data arrives.',
  },
  {
    id: 'scenarios',
    section: 'scenarios',
    title: 'Test what-ifs',
    prompt: () => 'Scenarios let you move a driver and watch the forecast react. Click Scenarios to try one.',
    explain: 'Pick a driver, set a change — say −20% — and run it. The impact shows against the baseline. Useful for planning before deciding.',
  },
  {
    id: 'alerts',
    section: 'alerts',
    title: 'Stay ahead with alerts',
    prompt: () => 'Last stop — Alerts watch the forecast for you, so you don\u2019t have to keep checking.',
    explain: 'Create one anytime: "drops sharply", "crosses a value", "anomaly detected". You\u2019ll get notified the moment it fires.',
  },
];

const readDone = () => {
  if (hasUiFlag('tour_done')) return true;
  try { return localStorage.getItem(DONE_KEY) === '1'; } catch (_e) { return true; }
};
const writeDone = () => {
  if (!hasUiFlag('tour_done')) setUiFlag('tour_done');
  try { localStorage.setItem(DONE_KEY, '1'); } catch (_e) { /* private mode */ }
};
const readAcks = (goalId) => {
  try { return new Set(JSON.parse(sessionStorage.getItem(ackKey(goalId)) || '[]')); } catch (_e) { return new Set(); }
};
const writeAcks = (goalId, acks) => {
  try { sessionStorage.setItem(ackKey(goalId), JSON.stringify([...acks])); } catch (_e) { /* private mode */ }
};

// Hooks for the first-build theater (BuildTheater.js), which runs INSTEAD
// of this tour while a goal's first build is live. When the theater ends
// it pre-acknowledges the sections it already walked (drivers/models/
// forecast) so this tour resumes at scenarios instead of re-teaching, or
// kills the tour outright when the user skipped the walkthrough.
export const ackGoalTourSteps = (goalId, ids) => {
  const next = readAcks(goalId);
  ids.forEach((id) => next.add(id));
  writeAcks(goalId, next);
};
export const markGoalTourDone = () => writeDone();

/**
 * Drives the tour. Returns the floating card element (or null) and the
 * nav key that should pulse (or null). Mount the card near the end of
 * the page; pass the highlight to GoalSideNav.
 */
export const useGoalTour = ({ goalId, section, activity, goal, suspended = false }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(readDone);
  const [acks, setAcks] = useState(() => readAcks(goalId));
  const [visited, setVisited] = useState(() => new Set());

  // Re-scope progress when the user lands on a different goal mid-tour.
  useEffect(() => {
    setAcks(readAcks(goalId));
    setVisited(new Set());
  }, [goalId]);

  // The first-build theater owns the stage while it runs and writes its
  // outcome (acks / done flags) straight to storage — re-sync when it
  // hands back so the tour resumes from the right stop, not from stale
  // mount-time state.
  useEffect(() => {
    if (suspended) return;
    setDismissed(readDone());
    setAcks(readAcks(goalId));
  }, [suspended, goalId]);

  const phases = useMemo(() => activity?.phases || [], [activity]);
  const phase = useCallback(
    (id) => phases.find((p) => p.id === id)?.state || 'pending',
    [phases],
  );
  const stats = activity?.stats || {};
  const driversValidated = stats.drivers?.validated ?? 0;
  const driversExploratory = stats.drivers?.exploratory ?? 0;
  const driversReady = phase('discover') === 'done' || driversValidated > 0;
  const fitStarted = ['active', 'review', 'done'].includes(phase('fit'));
  const servingReady = Boolean(stats.deploy?.live)
    || ['serving', 'live'].includes(goal?.status);

  const available = {
    drivers: driversReady,
    models: driversReady && fitStarted,
    forecast: servingReady,
    scenarios: servingReady,
    alerts: servingReady,
  };

  const active = Boolean(goal) && goal.goal_kind !== 'fleet'
    && goal.status !== 'failed' && !dismissed;
  const current = active
    ? STEPS.find((s) => !acks.has(s.id) && available[s.id]) || null
    : null;
  const allAcked = STEPS.every((s) => acks.has(s.id));

  // Once every stop is acknowledged the tour is over for good.
  useEffect(() => {
    if (active && allAcked) { writeDone(); setDismissed(true); }
  }, [active, allAcked]);

  // Arriving on the target section flips the card from "go there" to the
  // explanation - whether via our button or the user's own click.
  useEffect(() => {
    if (current && section === current.section) {
      setVisited((v) => (v.has(current.id) ? v : new Set(v).add(current.id)));
    }
  }, [current, section]);

  const skip = useCallback(() => {
    writeDone();
    setTour(null);   // a skip kills the platform walk too - never again
    setDismissed(true);
  }, []);
  const ack = useCallback((id) => {
    setAcks((prev) => {
      const next = new Set(prev).add(id);
      writeAcks(goalId, next);
      return next;
    });
  }, [goalId]);

  if (suspended || !active) return { tourCard: null, tourHighlight: null };

  const driversLabel = driversValidated
    ? `${driversValidated} driver${driversValidated === 1 ? '' : 's'}${driversExploratory ? ` (+${driversExploratory} suggestive)` : ''}`
    : 'the signals';

  let body = null;
  let highlight = null;

  if (current) {
    const onSection = visited.has(current.id) || section === current.section;
    const stepIdx = STEPS.findIndex((s) => s.id === current.id);
    const isLast = stepIdx === STEPS.length - 1;
    highlight = onSection ? null : current.section;
    body = (
      <TourCard
        key={`${current.id}-${onSection ? 'explain' : 'prompt'}`}
        title={current.title}
        text={onSection ? current.explain : current.prompt({ driversLabel })}
        progress={{ idx: stepIdx, total: STEPS.length }}
        onSkip={skip}
        action={onSection ? {
          // The last goal step flows straight into the platform tour: a
          // walk across the workspace pages, starting at Signals.
          label: isLast ? 'Continue tour' : 'Next',
          icon: <ArrowForwardIcon sx={{ fontSize: 15 }} />,
          onClick: () => {
            ack(current.id);
            if (isLast) {
              writeDone();
              setTour({ phase: 'platform', idx: 0 });
              navigate(PLATFORM_STEPS[0].path);
            }
          },
        } : {
          label: 'Show me',
          icon: <ArrowForwardIcon sx={{ fontSize: 15 }} />,
          onClick: () => navigate(`/goals/${goalId}/${current.section}`),
        }}
      />
    );
  } else if (!driversReady) {
    body = (
      <TourCard
        key="building"
        title="Building your goal"
        text="Discovery is finding which of your signals move the target. This takes a minute — the steps on the right fill in live."
        onSkip={skip}
      />
    );
  } else if (!servingReady) {
    // Drivers + models acknowledged; training still running.
    body = (
      <TourCard
        key="waiting"
        title="Training in progress"
        text={'The tournament is still running. I\u2019ll point you to the forecast the moment it goes live.'}
        onSkip={skip}
      />
    );
  }

  return { tourCard: body, tourHighlight: highlight };
};

export const TourCard = ({ title, text, progress, action, onSkip }) => (
  <Box
    sx={{
      position: 'fixed',
      left: { xs: 12, md: 88 },
      bottom: 18,
      zIndex: (t) => t.zIndex.drawer - 1,
      width: 316,
      maxWidth: 'calc(100vw - 100px)',
      p: 2,
      borderRadius: '14px',
      bgcolor: 'background.paper',
      border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.35)}`,
      boxShadow: (t) => `0 12px 40px ${alpha('#000', t.palette.mode === 'dark' ? 0.5 : 0.18)}`,
      animation: `${cardIn} 260ms cubic-bezier(0.34, 1.2, 0.44, 1) both`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
      <AutoAwesomeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 0 }}>
        {title}
      </Typography>
      <IconButton size="small" onClick={onSkip} aria-label="Skip tour" sx={{ p: 0.25, mr: -0.5, mt: -0.5 }}>
        <CloseIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
      </IconButton>
    </Stack>
    <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: 'text.secondary' }}>
      {text}
    </Typography>
    <Stack direction="row" alignItems="center" sx={{ mt: 1.5 }}>
      {progress ? (
        <Stack direction="row" spacing={0.5} sx={{ flex: 1 }} aria-label={`Step ${progress.idx + 1} of ${progress.total}`}>
          {Array.from({ length: progress.total }, (_v, i) => (
            <Box
              key={i}
              sx={{
                width: i === progress.idx ? 14 : 5,
                height: 5,
                borderRadius: 3,
                bgcolor: (t) => (i <= progress.idx ? t.palette.primary.main : alpha(t.palette.text.primary, 0.15)),
                transition: 'width 200ms ease',
              }}
            />
          ))}
        </Stack>
      ) : <Box sx={{ flex: 1 }} />}
      <Button
        size="small"
        onClick={onSkip}
        sx={{ fontSize: 11.5, color: 'text.secondary', textTransform: 'none', minWidth: 0, px: 0.75 }}
      >
        Skip tour
      </Button>
      {action && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          endIcon={action.icon}
          onClick={action.onClick}
          sx={{
            ml: 0.75, fontSize: 12, fontWeight: 600, textTransform: 'none',
            borderRadius: '8px', px: 1.25, py: 0.4,
          }}
        >
          {action.label}
        </Button>
      )}
    </Stack>
  </Box>
);

export default useGoalTour;
